import { HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
  IEmployeeDetailsResponse,
  IEmployeeListResponse,
} from '@carm/_core/employees/models';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { IBaseResponse } from '@carm/_utils/models';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IClientGroup,
  IClientGroupClientsApiResponse,
  IClientGroupEmployee,
  IClientGroupEmployeeListApiResponse,
  IClientGroupListApiResponse,
  IClientGroupProcessRequestsListApiResponse,
  IEmployeeClientGroupApiResponse,
  IGetClientGroupResponse,
  IModifyStaffMemberClientGroupRequest,
  IEmployeeClientGroupRolesApiResponse,
  IClientGroupProcessRequestsPendingCountApiResponse,
} from '../models';
import { LoaderSpinnerTypes } from '@carm/_core';

@Injectable()
export class ClientGroupService extends BaseApiService {
  constructor(protected injector: Injector) {
    super(injector);
  }

  public getClientGroupsList(loaderSpinnerType: LoaderSpinnerTypes): Observable<IClientGroupListApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const params = new HttpParams()
          .set('currentPage', '1')
          .set('pageSize', '1000000')
          .set('sort', 'name asc');

        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/service-provider?fields=BASIC`;
        return this.httpAuthGet<IClientGroupListApiResponse>(endpoint, params, loaderSpinnerType);
      })
    );
  }

  public getClientGroups(pageIndex: number, pageSize: number, sort: string, filterField: string, filterValue: string): Observable<IClientGroupListApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        let params = new HttpParams()
          .set('currentPage', pageIndex)
          .set('pageSize', pageSize)
          .set('sort', sort);

        if (filterField && filterValue) {
          params = params
            .set('filterField', filterField)
            .set('filterValue', filterValue);
        }

        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/service-provider?fields=BASIC`;
        return this.httpAuthGet<IClientGroupListApiResponse>(endpoint, params, LoaderSpinnerTypes.AgGrid);
      })
    );
  }

  public getClientGroup(uid: string): Observable<IGetClientGroupResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/${uid}`;
        return this.httpAuthGet<IGetClientGroupResponse>(endpoint);
      })
    );
  }

  public getClientGroupClients(
    uid: string,
    page: number,
    pageSize: number,
    filterField: string = '',
    filterValue: string = '',
    sort: string = 'legalName'
  ): Observable<IClientGroupClientsApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const params = new HttpParams()
          .set('currentPage', page.toString())
          .set('pageSize', pageSize.toString())
          .set('filterField', filterField)
          .set('filterValue', filterValue)
          .set('sort', sort);
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/${uid}/clients/list`;
        return this.httpAuthGet<IClientGroupClientsApiResponse>(
          endpoint,
          params,
          LoaderSpinnerTypes.AgGrid
        ).pipe(
          map((response: IClientGroupClientsApiResponse) => {
            response.totalRecords = response.pagination?.totalCount;
            return response;
          })
        );
      })
    );
  }

  public getClientGroupsEmployees(
    clientGroupId: string, pageIndex: number, pageSize: number, sort: string, filterField: string, filterValue: string
  ): Observable<IClientGroupEmployeeListApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        let params = this.setEmployeeParams(
          pageIndex,
          pageSize,
          sort,
          filterField,
          filterValue,
          clientGroupId
        );

        if (filterField && filterValue) {
          params = params
            .set('filterField', filterField)
            .set('filterValue', filterValue);
        }

        if (clientGroupId === null) {
          const endpoint = `${environment.baseApiUrls}/business/${businessUid}/employees/no-bam?fields=BASIC`;
          return this.httpAuthGet<IEmployeeListResponse>(endpoint, params, LoaderSpinnerTypes.AgGrid).pipe(
            map((response: IEmployeeListResponse) => {
              return {
                pagination: response.pagination,
                results: response.results.map(
                  (employee: IEmployeeDetailsResponse) => {
                    return {
                      uid: employee.uid,
                      name: employee.name,
                      email: employee.email,
                    } as IClientGroupEmployee;
                  }
                ),
              } as IClientGroupEmployeeListApiResponse;
            })
          );
        } else {
          const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/${clientGroupId}/employees?fields=BASIC`;
          return this.httpAuthGet<IClientGroupEmployeeListApiResponse>(
            endpoint,
            params,
            LoaderSpinnerTypes.AgGrid
          ).pipe(
            map((response: IClientGroupEmployeeListApiResponse) => {
              return {
                pagination: response.pagination,
                results: response.results?.map(
                  (employee: IClientGroupEmployee) => {
                    return {
                      ...employee,
                      name: `${employee.firstName} ${employee.lastName}`,
                    } as IClientGroupEmployee;
                  }
                ),
              } as IClientGroupEmployeeListApiResponse;
            })
          );
        }
      })
    );
  }

  public getClientGroupsProcessRequests(currentPage: number, pageSize: number, sort: string, filterField: string, filterValue: string): Observable<IClientGroupProcessRequestsListApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        let params = new HttpParams()
          .set('currentPage', currentPage)
          .set('pageSize', pageSize)
          .set('sort', sort)
          .set('serviceProviderUid', businessUid);

        if (filterField && filterValue) {
          params = params
            .set('filterField', filterField)
            .set('filterValue', filterValue);
        }
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/process?fields=DEFAULT`;
        return this.httpAuthGet<IClientGroupProcessRequestsListApiResponse>(
          endpoint,
          params,
          LoaderSpinnerTypes.AgGrid
        );
      })
    );
  }

  public getClientGroupsProcessPendingCount(): Observable<IClientGroupProcessRequestsPendingCountApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/process-pending-count`;
        return this.httpAuthGet<IClientGroupProcessRequestsPendingCountApiResponse>(endpoint);
      })
    );
  }

  public getAvailableClients(
    page: number,
    pageSize: number,
    filterField: string = '',
    filterValue: string = '',
    sort: string = 'legalName'
  ): Observable<IClientGroupClientsApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const params = new HttpParams()
          .set('currentPage', page.toString())
          .set('pageSize', pageSize.toString())
          .set('filterField', filterField)
          .set('filterValue', filterValue)
          .set('sort', sort);
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/client/to-add?fields=BASIC`;
        return this.httpAuthGet<IClientGroupClientsApiResponse>(
          endpoint,
          params,
          LoaderSpinnerTypes.AgGrid
        );
      })
    );
  }

  public addOrUpdateStaffMemberClientGroup(
    clientGroupId: string,
    role: string,
    staffMembers: IClientGroupEmployee[]
  ): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const staffMemberUids = [];
        staffMembers.forEach(staffMember =>
          staffMemberUids.push(staffMember.uid)
        );

        const payload: IModifyStaffMemberClientGroupRequest = {
          clientGroupId,
          role,
          staffMembers: staffMemberUids,
        };

        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/${clientGroupId}/staff`;
        return this.httpAuthPost<IBaseResponse>(endpoint, payload);
      })
    );
  }

  public deleteClientGroup(uid: string): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const params = new HttpParams().set('clientGroupUid', uid);

        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/${uid}`;
        return this.httpAuthDelete<IBaseResponse>(endpoint, params);
      })
    );
  }

  public validateName(clientGroupName: string): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/name/validation`;
        const params = new HttpParams().set('clientGroupName', clientGroupName);
        return this.httpAuthGet<IBaseResponse>(endpoint, params);
      })
    );
  }

  public saveClientGroup(clientGroup: IClientGroup): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group`;

        if (!clientGroup.uid) {
          return this.httpAuthPost<IBaseResponse>(endpoint, {
            name: clientGroup.name,
            clientsToAdd: clientGroup.selector?.selected?.list,
          });
        } else {
          return this.httpAuthPut<IBaseResponse>(endpoint, {
            uid: clientGroup.uid,
            name: clientGroup.name,
            clientsToAdd: clientGroup.selector?.selected?.list,
            clientsToRemove: clientGroup.removedSelector?.selected?.list,
          });
        }
      })
    );
  }

  public removeStaffMemberFromClientGroup(
    clientGroupId: string,
    role: string,
    staffMembers: IClientGroupEmployee[]
  ): Observable<IBaseResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const staffMemberUids = [];
        staffMembers.forEach(staffMember =>
          staffMemberUids.push(staffMember.uid)
        );
        const payload: IModifyStaffMemberClientGroupRequest = {
          clientGroupId,
          role,
          staffMembers: staffMemberUids,
        };

        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/${clientGroupId}/staff/to-delete`;
        return this.httpAuthPost<IBaseResponse>(endpoint, payload);
      })
    );
  }

  public getEmployeeClientGroups(
    employeeId: string,
    loaderSpinnerType: LoaderSpinnerTypes
  ): Observable<IEmployeeClientGroupApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const params = new HttpParams()
          .set('employeeId', employeeId)
          .set('currentPage', '1')
          .set('pageSize', '1000000')
          .set('sort', 'name');
        const endpoint = `${environment.baseApiUrls}/business/${businessUid}/client-group/employee?fields=BASIC`;
        return this.httpAuthGet<IEmployeeClientGroupApiResponse>(
          endpoint,
          params,
          loaderSpinnerType
        );
      }),
      map(response => {
        response.results.forEach(employee => {
          employee.defaultRole = this.translateService.instant(
            `concepts.access_group.${employee.defaultRole.toLowerCase()}`
          );
        });
        return response;
      })
    );
  }

  public getEmployeeProgramRoles(
    employeeId: string
  ): Observable<IEmployeeClientGroupRolesApiResponse> {
    return this.userService.getUserBusinessId().pipe(
      mergeMap(businessUid => {
        const params = new HttpParams()
          .set('currentPage', '1')
          .set('pageSize', '1000000')
          .set('sort', 'businessName');
        const endpoint = `${
          environment.baseApiUrls
        }/business/${businessUid}/client-group/employee/${encodeURIComponent(
          employeeId
        )}/program-account?fields=DEFAULT`;
        return this.httpAuthGet<IEmployeeClientGroupRolesApiResponse>(
          endpoint,
          params
        );
      })
    );
  }

  private setEmployeeParams(
    pageIndex: number | null | undefined,
    pageSize: number | null | undefined,
    sort: string,
    filterField?: string,
    filterValue?: string,
    clientGroupId?: string
  ): HttpParams {
    const safePageIndex = pageIndex ?? 0;
    const safePageSize = pageSize ?? 100;
    let params = new HttpParams()
      .set('currentPage', String(safePageIndex))
      .set('pageSize', String(safePageSize))
      .set('sort', sort ?? 'name_ASC')
      .set('fields', 'BASIC');

    if (clientGroupId) params = params.set('clientGroupUid', clientGroupId);
    if (filterField && filterValue) {
      params = params
        .set('filterField', filterField)
        .set('filterValue', filterValue);
    }
    return params;
  }
}
